import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchBillingContext, createCheckout, createPortal, BillingStatus } from '../api/billing';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import LocaleSwitcher from '../components/LocaleSwitcher';

const STATUS_CLASS: Record<BillingStatus, string> = {
  none: 'ep-badge--neutral',
  active: 'ep-badge--green',
  trialing: 'ep-badge--blue',
  past_due: 'ep-badge--orange',
  canceled: 'ep-badge--red',
  unpaid: 'ep-badge--red',
};

export default function DashboardPage() {
  const { tx } = useI18n();
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing-context'],
    queryFn: fetchBillingContext,
    retry: 1,
  });

  function handleLogout() {
    clearAuth();
    navigate('/login');
  }

  async function handleSubscribe() {
    const base = import.meta.env.VITE_PORTAL_BASE_URL as string ?? window.location.origin;
    const { url } = await createCheckout({
      successUrl: `${base}/?checkout=success`,
      cancelUrl: `${base}/?checkout=cancel`,
      contactEmail: data?.editor,
    });
    window.location.href = url;
  }

  async function handleManage() {
    const base = import.meta.env.VITE_PORTAL_BASE_URL as string ?? window.location.origin;
    const { url } = await createPortal({
      returnUrl: base,
      contactEmail: data?.editor,
    });
    window.location.href = url;
  }

  return (
    <div className="ep-page">
      <header className="ep-dashboard__header">
        <span className="ep-logo">Permio</span>
        <div className="ep-dashboard__header-actions">
          <LocaleSwitcher />
          <button className="ep-btn ep-btn--ghost" onClick={handleLogout}>
            {tx.dashboard.logout}
          </button>
        </div>
      </header>

      <main className="ep-dashboard__main">
        <h1 className="ep-dashboard__title">{tx.dashboard.title}</h1>

        {isLoading && (
          <p className="ep-muted">{tx.dashboard.loadingCtx}</p>
        )}

        {isError && (
          <p className="ep-error">{tx.dashboard.errorCtx}</p>
        )}

        {data && (
          <>
            <div className="ep-card">
              <div className="ep-stat-row">
                <span className="ep-stat-label">{tx.dashboard.editor}</span>
                <code className="ep-code">{data.editor}</code>
              </div>

              <div className="ep-stat-row">
                <span className="ep-stat-label">{tx.dashboard.status}</span>
                <span className={`ep-badge ${STATUS_CLASS[data.status]}`}>
                  {tx.dashboard.statusLabel[data.status]}
                </span>
              </div>

              <div className="ep-stat-row">
                <span className="ep-stat-label">{tx.dashboard.seats}</span>
                <span className="ep-stat-value">{data.currentSeatCount}</span>
              </div>
            </div>

            {!data.stripeEnabled ? (
              <p className="ep-muted">{tx.dashboard.noStripe}</p>
            ) : (
              <div className="ep-actions">
                {data.status === 'none' || data.status === 'canceled' ? (
                  <button className="ep-btn ep-btn--primary" onClick={handleSubscribe}>
                    {tx.dashboard.subscribe}
                  </button>
                ) : (
                  <button className="ep-btn ep-btn--primary" onClick={handleManage}>
                    {tx.dashboard.manage}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
